import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Bot,
  CheckCircle2,
  Code,
  Copy,
  Globe,
  KeyRound,
  Layers,
  Loader2,
  Plus,
  RefreshCcw,
  Smartphone,
  Sparkles,
  Trash2,
  TreePine,
  Waves,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AgentPageShell from '../../components/agent/AgentPageShell';
import PhoneMockup from '../../components/agent/PhoneMockup';
import CodeSnippet from '../../components/agent/CodeSnippet';
import { cn } from '../../lib/utils';
import {
  createChannel,
  deleteChannel,
  fetchChannels,
  rotateChannelKey,
  updateChannel,
} from '../../lib/agentApi';
import { BACKEND_URL } from '../../config';

const ICONS = {
  tree: TreePine,
  spark: Sparkles,
  wave: Waves,
  robot: Bot,
};

const PLATFORMS = [
  { platform: 'Web (script tag)', min: 'Any modern browser', status: 'Supported', notes: 'Shadow DOM widget' },
  { platform: 'Web (iframe)', min: 'Any modern browser', status: 'Supported', notes: 'Strict isolation' },
  { platform: 'React / Next.js', min: 'React 17+', status: 'Supported', notes: 'Wrapper component' },
  { platform: 'Ionic (Angular)', min: 'Ionic 6+', status: 'Supported', notes: 'Wraps the web bundle' },
  { platform: 'React Native', min: '0.70+', status: 'Planned', notes: 'Native overlay + audio' },
  { platform: 'Flutter', min: '3.0+', status: 'Planned', notes: 'Stable channel' },
  { platform: 'Node.js / PHP / Python', min: 'Server SDKs', status: 'Supported', notes: 'REST + OpenAPI' },
];

const WidgetDefaultPage = () => (
  <AgentPageShell
    title="Mobile Widget — Default State"
    description="Deployment channels, keys and the floating widget your users tap"
    icon={Smartphone}
    breadcrumb={['Mobile Widget', 'Default State']}
  >
    {({ agent, orgId }) => <WidgetBody key={agent.id} agent={agent} orgId={orgId} />}
  </AgentPageShell>
);

const WidgetBody = ({ agent, orgId }) => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [iconStyle, setIconStyle] = useState('tree');
  const [size, setSize] = useState('md');
  const [position, setPosition] = useState('bottom-right');
  const [glass, setGlass] = useState(80);
  const [originDraft, setOriginDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchChannels(orgId, agent.id);
      setChannels(result.channels || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load channels');
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id]);

  useEffect(() => {
    load();
  }, [load]);

  const addChannel = async (type) => {
    setCreating(true);
    try {
      await createChannel(orgId, agent.id, {
        type,
        widget_config: { icon_style: iconStyle, size, position, glass },
      });
      toast.success('Channel created with a new publishable key');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create the channel');
    } finally {
      setCreating(false);
    }
  };

  const rotate = async (channelId) => {
    try {
      await rotateChannelKey(orgId, agent.id, channelId);
      toast.success('Key rotated — update your apps with the new key');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not rotate the key');
    }
  };

  const remove = async (channelId) => {
    try {
      await deleteChannel(orgId, agent.id, channelId);
      toast.success('Channel deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete the channel');
    }
  };

  const addOrigin = async (channel) => {
    const origin = (originDraft[channel.id] || '').trim();
    if (!origin) return;
    try {
      await updateChannel(orgId, agent.id, channel.id, {
        allowed_origins: [...new Set([...(channel.allowed_origins || []), origin])],
      });
      setOriginDraft((d) => ({ ...d, [channel.id]: '' }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the origin');
    }
  };

  const removeOrigin = async (channel, origin) => {
    try {
      await updateChannel(orgId, agent.id, channel.id, {
        allowed_origins: (channel.allowed_origins || []).filter((o) => o !== origin),
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove the origin');
    }
  };

  const key = channels[0]?.publishable_key || 'dt_pk_YOUR_KEY';
  const snippets = buildSnippets(key, BACKEND_URL, position);

  return (
    <div className="space-y-8">
      {/* Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 py-10 bg-muted/30 rounded-3xl border border-dashed border-border">
        <PhoneMockup label="Light — LTR (English)">
          <MockApp isRTL={false} />
          <FloatingButton
            iconStyle={iconStyle}
            size={size}
            position={position === 'bottom-left' ? 'bottom-left' : 'bottom-right'}
            glass={glass}
          />
        </PhoneMockup>
        <PhoneMockup label="Dark — RTL (Arabic)" isDark isRTL>
          <MockApp isRTL isDark />
          <FloatingButton
            iconStyle={iconStyle}
            size={size}
            position={position === 'bottom-right' ? 'bottom-left' : 'bottom-right'}
            glass={glass}
            isDark
          />
        </PhoneMockup>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Appearance */}
        <Card>
          <CardContent className="p-6 space-y-6">
            <h3 className="font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Widget Appearance
            </h3>

            <div className="space-y-3">
              <Label className="text-sm">Position</Label>
              <div className="grid grid-cols-2 gap-2">
                {['bottom-right', 'bottom-left'].map((pos) => (
                  <button
                    key={pos}
                    onClick={() => setPosition(pos)}
                    className={cn(
                      'flex items-center gap-2 h-12 px-3 text-xs font-medium rounded-lg border transition-all',
                      position === pos
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-background border-border text-muted-foreground hover:border-muted-foreground/40'
                    )}
                  >
                    <span className="relative w-8 h-8 rounded border border-dashed border-current/40">
                      <span
                        className={cn(
                          'absolute w-1.5 h-1.5 rounded-full bg-current bottom-1',
                          pos === 'bottom-right' ? 'right-1' : 'left-1'
                        )}
                      />
                    </span>
                    {pos === 'bottom-right' ? 'Bottom right' : 'Bottom left'}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm">Size</Label>
              <Tabs value={size} onValueChange={setSize}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="sm">Small</TabsTrigger>
                  <TabsTrigger value="md">Medium</TabsTrigger>
                  <TabsTrigger value="lg">Large</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="space-y-3">
              <Label className="text-sm">Icon style</Label>
              <div className="grid grid-cols-4 gap-3">
                {Object.entries(ICONS).map(([id, Icon]) => (
                  <button
                    key={id}
                    onClick={() => setIconStyle(id)}
                    className={cn(
                      'flex flex-col items-center gap-2 p-3 rounded-xl border transition-all',
                      iconStyle === id
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border text-muted-foreground hover:border-muted-foreground/40'
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[10px] font-bold uppercase">{id}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <Label className="text-sm">Glassmorphism intensity</Label>
                <span className="text-xs text-muted-foreground">{glass}%</span>
              </div>
              <Slider value={[glass]} onValueChange={([v]) => setGlass(v)} max={100} step={1} />
            </div>
          </CardContent>
        </Card>

        {/* Channels */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-primary" />
                Deployment Channels
              </h3>
              <Select onValueChange={addChannel} disabled={creating}>
                <SelectTrigger className="w-[150px] h-9">
                  <SelectValue placeholder="Add channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="web_script">Web script</SelectItem>
                  <SelectItem value="iframe">Iframe</SelectItem>
                  <SelectItem value="mobile">Mobile app</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Each channel gets its own publishable key scoped to <strong>this agent only</strong>,
              so a key leaked from a public app cannot reach another agent's documentation.
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading channels…
              </div>
            ) : channels.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-8 text-center">
                <Globe className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium">No channels yet</p>
                <p className="text-xs text-muted-foreground mt-1 mb-4">
                  Create one to generate a key and embed this agent.
                </p>
                <Button size="sm" onClick={() => addChannel('web_script')} disabled={creating}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create web channel
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {channels.map((channel) => (
                  <div key={channel.id} className="rounded-lg border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{channel.name}</p>
                        <p className="text-[11px] text-muted-foreground capitalize">
                          {channel.type.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Switch
                          checked={channel.enabled}
                          onCheckedChange={(v) =>
                            updateChannel(orgId, agent.id, channel.id, { enabled: v }).then(load)
                          }
                        />
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rotate(channel.id)} title="Rotate key">
                          <RefreshCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => remove(channel.id)}
                          title="Delete channel"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[11px] font-mono bg-muted px-2 py-1.5 rounded truncate">
                        {channel.publishable_key}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => {
                          navigator.clipboard?.writeText(channel.publishable_key);
                          toast.success('Key copied');
                        }}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        Allowed origins
                      </Label>
                      <div className="flex flex-wrap gap-1.5">
                        {(channel.allowed_origins || []).map((origin) => (
                          <Badge key={origin} variant="secondary" className="gap-1 pl-2 pr-1 text-[11px]">
                            {origin}
                            <button onClick={() => removeOrigin(channel, origin)} className="hover:text-destructive">
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                        {(channel.allowed_origins || []).length === 0 && (
                          <span className="text-[11px] text-amber-600 dark:text-amber-400">
                            No origin restriction — any site can use this key.
                          </span>
                        )}
                      </div>
                      <Input
                        placeholder="https://app.example.com — press Enter"
                        className="h-8 text-xs"
                        value={originDraft[channel.id] || ''}
                        onChange={(e) =>
                          setOriginDraft((d) => ({ ...d, [channel.id]: e.target.value }))
                        }
                        onKeyDown={(e) => e.key === 'Enter' && addOrigin(channel)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Integration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Code className="w-4 h-4 text-primary" />
            Integration
          </h3>
          <CodeSnippet snippets={snippets} />
          <p className="text-xs text-muted-foreground leading-relaxed">
            The script and iframe embeds run today's REST API. Server SDKs are generated from the
            backend's OpenAPI spec, so they track the API automatically.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            Platform Support
          </h3>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Platform</TableHead>
                  <TableHead>Requires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {PLATFORMS.map((row) => (
                  <TableRow key={row.platform}>
                    <TableCell className="font-medium text-sm">{row.platform}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{row.min}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-xs">
                        {row.status === 'Supported' ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        )}
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {row.notes}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  );
};

const FloatingButton = ({ iconStyle, size, position, glass, isDark }) => {
  const Icon = ICONS[iconStyle] || Bot;
  const sizes = { sm: 'w-11 h-11', md: 'w-14 h-14', lg: 'w-16 h-16' };
  return (
    <div
      className={cn(
        'absolute z-40 bottom-8',
        position === 'bottom-right' ? 'right-5' : 'left-5'
      )}
    >
      <div className="absolute inset-0 rounded-full bg-primary/30 animate-pulse-ring" />
      <button
        className={cn(
          'relative flex items-center justify-center rounded-full animate-float shadow-lg',
          sizes[size],
          isDark ? 'glass-dark' : 'glass'
        )}
        style={{ backdropFilter: `blur(${Math.round(glass / 6)}px) saturate(180%)` }}
      >
        <Icon className={cn('w-6 h-6', isDark ? 'text-primary' : 'text-brand-blue')} />
        <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[8px] font-bold px-1 rounded-full border border-background">
          AI
        </span>
      </button>
    </div>
  );
};

const MockApp = ({ isRTL, isDark }) => (
  <div className="flex flex-col h-full">
    <div
      className={cn(
        'px-5 pt-12 pb-5 border-b',
        isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'
      )}
    >
      <h1 className={cn('text-lg font-bold mb-4', isDark ? 'text-white' : 'text-slate-900')}>
        {isRTL ? 'تطبيق العناية بالسيارات' : 'Car Care App'}
      </h1>
      <div
        className={cn(
          'px-3 py-2 rounded-xl text-sm',
          isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
        )}
      >
        {isRTL ? 'بحث عن خدمات...' : 'Search services...'}
      </div>
    </div>
    <div className="flex-1 p-5 space-y-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className={cn(
            'p-4 rounded-2xl border flex items-center gap-3',
            isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200 shadow-sm'
          )}
        >
          <div className="w-10 h-10 rounded-xl bg-primary/10" />
          <div className="flex-1 space-y-2">
            <div className={cn('h-3 w-20 rounded', isDark ? 'bg-slate-800' : 'bg-slate-100')} />
            <div className={cn('h-2 w-28 rounded', isDark ? 'bg-slate-800/60' : 'bg-slate-50')} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

function buildSnippets(key, backendUrl, position) {
  const api = `${backendUrl || 'https://api.doc-tree.com'}/api/v1/agent`;
  return {
    script: {
      label: 'Script tag',
      code: `<script
  src="${backendUrl || 'https://cdn.doc-tree.com'}/agent.js"
  data-agent-key="${key}"
  data-position="${position}"
  data-theme="auto"
  defer
></script>`,
    },
    iframe: {
      label: 'Iframe',
      code: `<iframe
  src="${backendUrl || 'https://app.doc-tree.com'}/agent/embed?key=${key}"
  style="border:0;position:fixed;${position === 'bottom-right' ? 'right' : 'left'}:24px;bottom:24px;width:400px;height:620px;z-index:9999"
  allow="microphone"
  title="DocTree Agent"
></iframe>`,
    },
    react: {
      label: 'React',
      code: `import { useEffect } from 'react';

export function DocTreeAgent() {
  useEffect(() => {
    const s = document.createElement('script');
    s.src = '${backendUrl || 'https://cdn.doc-tree.com'}/agent.js';
    s.dataset.agentKey = '${key}';
    s.defer = true;
    document.body.appendChild(s);
    return () => s.remove();
  }, []);
  return null;
}`,
    },
    ionic: {
      label: 'Ionic / Angular',
      code: `import { Component, OnInit } from '@angular/core';

@Component({ selector: 'app-agent', template: '' })
export class AgentComponent implements OnInit {
  ngOnInit(): void {
    const s = document.createElement('script');
    s.src = '${backendUrl || 'https://cdn.doc-tree.com'}/agent.js';
    s.setAttribute('data-agent-key', '${key}');
    s.defer = true;
    document.body.appendChild(s);
  }
}`,
    },
    rn: {
      label: 'React Native',
      code: `import { WebView } from 'react-native-webview';

export default function AgentSheet() {
  return (
    <WebView
      source={{ uri: '${backendUrl || 'https://app.doc-tree.com'}/agent/embed?key=${key}' }}
      mediaPlaybackRequiresUserAction={false}
      allowsInlineMediaPlayback
    />
  );
}`,
    },
    flutter: {
      label: 'Flutter',
      code: `import 'package:webview_flutter/webview_flutter.dart';

final controller = WebViewController()
  ..setJavaScriptMode(JavaScriptMode.unrestricted)
  ..loadRequest(Uri.parse(
    '${backendUrl || 'https://app.doc-tree.com'}/agent/embed?key=${key}',
  ));`,
    },
    node: {
      label: 'Node.js',
      code: `const res = await fetch('${api}/chat', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Agent-Key': '${key}',
  },
  body: JSON.stringify({ message: 'How do I request a tow?' }),
});

const data = await res.json();
console.log(data.message.content, data.confidence);`,
    },
    php: {
      label: 'PHP',
      code: `<?php
$ch = curl_init('${api}/chat');
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'Content-Type: application/json',
        'X-Agent-Key: ${key}',
    ],
    CURLOPT_POSTFIELDS => json_encode(['message' => 'How do I request a tow?']),
]);

$data = json_decode(curl_exec($ch), true);
echo $data['message']['content'];`,
    },
    python: {
      label: 'Python',
      code: `import requests

response = requests.post(
    "${api}/chat",
    headers={"X-Agent-Key": "${key}"},
    json={"message": "How do I request a tow?"},
    timeout=30,
)

data = response.json()
print(data["message"]["content"], data["confidence"])`,
    },
  };
}

export default WidgetDefaultPage;
